import { useI18n } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import { Car, Paintbrush, Wrench, Truck, Settings, Hammer } from "lucide-react";

const ICONS = { car: Car, paintbrush: Paintbrush, wrench: Wrench, truck: Truck, settings: Settings, hammer: Hammer };

export default function Services() {
  const { t, pickSuffix } = useI18n();
  const { services } = useSite();

  return (
    <section id="services" data-testid="services" className="bg-black py-20 sm:py-28 border-b border-zinc-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="tech-label mb-4 !text-brand-red">{t("services.label")}</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight chrome-text">
            {t("services.title")}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-zinc-400">{t("services.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-zinc-800">
          {services.map((s, idx) => {
            const Icon = ICONS[s.icon] || Wrench;
            return (
              <article
                key={s.id}
                data-testid={`service-${idx}`}
                className="group relative bg-zinc-950 border-r border-b border-zinc-800 overflow-hidden hover:bg-zinc-900 transition-colors duration-200"
              >
                {s.image_url ? (
                  <div className="aspect-[4/3] overflow-hidden bg-zinc-900 relative">
                    <img
                      src={s.image_url}
                      alt={pickSuffix(s, "title")}
                      className="w-full h-full object-cover photo-enhance transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
                  </div>
                ) : (
                  <div className="aspect-[4/3] flex items-center justify-center bg-gradient-to-br from-zinc-900 via-black to-zinc-900 relative overflow-hidden">
                    <div className="absolute inset-0 opacity-[0.04]" style={{
                      backgroundImage:
                        "repeating-linear-gradient(45deg, #fff 0, #fff 1px, transparent 1px, transparent 12px)"
                    }} />
                    <Icon size={72} strokeWidth={1.4} className="text-brand-red/80 relative z-10" />
                    <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-brand-red/60 to-transparent" />
                  </div>
                )}
                <div className="p-6 sm:p-7">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-9 w-9 bg-brand-red text-white flex items-center justify-center">
                      <Icon size={18} strokeWidth={2.4} />
                    </div>
                    <span className="tech-label !text-[10px]">0{idx + 1}</span>
                  </div>
                  <h3 className="font-display font-extrabold text-xl sm:text-2xl tracking-tight text-white">
                    {pickSuffix(s, "title")}
                  </h3>
                  <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                    {pickSuffix(s, "description")}
                  </p>
                </div>
                <div className="absolute top-0 right-0 h-1 w-12 bg-brand-red transition-all duration-200 group-hover:w-full" />
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
